'use client';

import { type RefObject } from 'react';

import type { BasePaginationProps } from '@/app/components/navigation/pagination/types/pagination-type';
import { cn } from '@/app/utils/cn';
import { cnva } from '@/app/utils/cnva';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { safeCall } from '@/app/utils/safeCall';

import styles from './index.module.css';

const PaginationDashStyled = cnva(
  [
    'transition-bg relative z-0 h-1 w-6 xl:w-10',
    'before:absolute before:inset-x-0 before:-inset-y-3.5',
  ],
  {
    variants: {
      theme: {
        light: '',
        dark: '',
      },
      isActive: {
        true: 'pointer-events-none',
        false: 'cursor-pointer',
      },
      isAnimated: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        theme: 'light',
        isActive: false,
        className: 'bg-white',
      },
      {
        theme: 'light',
        isActive: true,
        isAnimated: true,
        className: 'bg-white',
      },
      {
        theme: 'dark',
        isActive: false,
        className: 'bg-gray-500',
      },
      {
        theme: 'dark',
        isActive: true,
        isAnimated: true,
        className: 'bg-gray-500',
      },
      {
        isActive: true,
        isAnimated: false,
        className: 'bg-red-500',
      },
    ],
    defaultVariants: {
      theme: 'light',
      isActive: false,
    },
  }
);

export interface PaginationDashProps extends BasePaginationProps {
  itemRefs?: RefObject<(HTMLDivElement | null)[]>;
  progressClassName?: string;
  isAnimated?: boolean;
}

export const PaginationDash = ({
  itemRefs,
  progressClassName,
  wrapperClassName,
  className,
  theme = 'light',
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  isAnimated = false,
  ...props
}: PaginationDashProps) => {
  const handlePageChange = (page: number) => {
    if (page === currentPage) return;

    safeCall(onPageChange, page);
  };

  return (
    <nav
      role="navigation"
      aria-label="Pagination navigation"
      className={cn('flex items-center gap-x-4', wrapperClassName)}
      {...createDataAttributes({
        test: 'pagination-dash',
      })}
    >
      {Array.from({ length: totalPages }, (_, index) => {
        const page = index + 1;
        const isActive = page === currentPage;
        return (
          <button
            type="button"
            key={index}
            className={PaginationDashStyled({
              theme,
              isActive,
              isAnimated,
              className,
            })}
            aria-current={isActive ? 'page' : undefined}
            aria-label={`Page ${page}`}
            {...props}
            {...createDataAttributes({
              test: `pagination-${page}`,
              active: isActive ? 'true' : null,
            })}
            onClick={() => handlePageChange(page)}
          >
            {!!isActive && !!isAnimated && (
              <div
                className="absolute inset-0 overflow-hidden"
                aria-hidden="true"
              >
                <div
                  ref={(el) => {
                    if (itemRefs?.current) itemRefs.current[index] = el;
                  }}
                  className={cn(
                    'absolute inset-0 w-full bg-red-500',
                    styles.paginationDashAnimated,
                    progressClassName
                  )}
                />
              </div>
            )}
          </button>
        );
      })}
    </nav>
  );
};
